import { useCallback, useMemo, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useMutation } from '@apollo/client';

import {
  CreateMMilestonesTemplateMutation,
  CreateMMilestonesTemplateMutationVariables,
  DeleteMMilestonesTemplateMutation,
  DeleteMMilestonesTemplateMutationVariables,
  ItemAvailability,
  LevelRewardInput,
  MarathonMilestoneTemplate,
  UpdateMMilestonesTemplateMutation,
  UpdateMMilestonesTemplateMutationVariables,
} from '../../../../../../__gqltypes__';
import { LoadingLogo } from '../../../../devtools/components/Modal';
import EditRewardsModal from '../../../components/EditRewardsModal';
import {
  CREATE_MARATHON_MILESTONE_TEMPLATE,
  DELETE_MARATHON_MILESTONE_TEMPLATE,
  UPDATE_MARATHON_MILESTONE_TEMPLATE,
} from '../graphql';
import {
  getMilestoneRowErrors,
  milestoneConfigToRuntime,
  milestoneToInput,
  MMilestone,
  newMilestone,
} from '../utils';
import { MarathonMilestonesTable } from './MarathonMilestonesTable';

const NONE = 'None';
const MILESTONE_STEP = 10;

type RewardTarget = {
  milestoneIndex: number;
  isPlus: boolean;
};

type Props = {
  templates: MarathonMilestoneTemplate[];
  templatesLoading: boolean;
  onTemplatesChanged: () => void;
};

export function MarathonTemplatesData({ templates, templatesLoading, onTemplatesChanged }: Props): JSX.Element {
  const [editTemplateId, setEditTemplateId] = useState<string>(NONE);
  const [templateName, setTemplateName] = useState('');
  const [milestones, setMilestones] = useState<MMilestone[]>([]);
  const [rewardTarget, setRewardTarget] = useState<RewardTarget | null>(null);

  const [createTemplateMutation, { loading: createLoading }] = useMutation<
    CreateMMilestonesTemplateMutation,
    CreateMMilestonesTemplateMutationVariables
  >(CREATE_MARATHON_MILESTONE_TEMPLATE, {
    onCompleted: onTemplatesChanged,
  });

  const [updateTemplateMutation, { loading: updateLoading }] = useMutation<
    UpdateMMilestonesTemplateMutation,
    UpdateMMilestonesTemplateMutationVariables
  >(UPDATE_MARATHON_MILESTONE_TEMPLATE, {
    onCompleted: onTemplatesChanged,
  });

  const [deleteTemplateMutation, { loading: deleteLoading }] = useMutation<
    DeleteMMilestonesTemplateMutation,
    DeleteMMilestonesTemplateMutationVariables
  >(DELETE_MARATHON_MILESTONE_TEMPLATE, {
    onCompleted: onTemplatesChanged,
  });

  const handleSelectTemplate = useCallback(
    (id: string) => {
      const template = templates.find((t) => t.id === id);
      if (template) {
        setEditTemplateId(id);
        setTemplateName(template.name);
        setMilestones(milestoneConfigToRuntime(template.milestones));
      } else {
        setEditTemplateId(NONE);
        setTemplateName('');
        setMilestones([]);
      }
    },
    [templates]
  );

  const handleChangeMilestone = useCallback((index: number, patch: Partial<MMilestone>) => {
    setMilestones((prev) => prev.map((mile, i) => (i === index ? { ...mile, ...patch } : mile)));
  }, []);

  const handleEditReward = useCallback((milestoneIndex: number, isPlus: boolean) => {
    setRewardTarget({ milestoneIndex, isPlus });
  }, []);

  const handleSaveReward = useCallback(
    (reward: LevelRewardInput) => {
      if (!rewardTarget) {
        return;
      }
      handleChangeMilestone(
        rewardTarget.milestoneIndex,
        rewardTarget.isPlus ? { plusReward: { ...reward } } : { basicReward: { ...reward } }
      );
      setRewardTarget(null);
    },
    [rewardTarget, handleChangeMilestone]
  );

  const handleRemoveMilestone = useCallback((index: number) => {
    setMilestones((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleAddMilestone = useCallback(() => {
    setMilestones((prev) => {
      const max = prev.reduce((acc, mile) => Math.max(acc, mile.numberOfGuesses), 0);
      return prev.concat(newMilestone(max + MILESTONE_STEP));
    });
  }, []);

  const handleCreate = useCallback(() => {
    createTemplateMutation({
      variables: {
        input: {
          name: templateName,
          milestones: milestones.map(milestoneToInput),
        },
      },
    });
  }, [createTemplateMutation, templateName, milestones]);

  const handleUpdate = useCallback(() => {
    updateTemplateMutation({
      variables: {
        input: {
          id: editTemplateId,
          name: templateName,
          milestones: milestones.map(milestoneToInput),
        },
      },
    });
  }, [updateTemplateMutation, editTemplateId, templateName, milestones]);

  const handleDelete = useCallback(() => {
    if (window.confirm(`Delete template "${templateName}"? This cannot be undone.`)) {
      deleteTemplateMutation({
        variables: {
          id: editTemplateId,
        },
      });
      setEditTemplateId(NONE);
      setTemplateName('');
      setMilestones([]);
    }
  }, [deleteTemplateMutation, editTemplateId, templateName]);

  const rowErrors = useMemo(() => getMilestoneRowErrors(milestones, 0), [milestones]);

  const selectedReward = useMemo<LevelRewardInput | null>(() => {
    if (!rewardTarget) {
      return null;
    }
    const milestone = milestones[rewardTarget.milestoneIndex];
    if (!milestone) {
      return null;
    }
    return rewardTarget.isPlus ? milestone.plusReward : milestone.basicReward;
  }, [milestones, rewardTarget]);

  const loading = templatesLoading || createLoading || updateLoading || deleteLoading;

  return (
    <Card style={{ width: '100%' }}>
      <EditRewardsModal
        isEdit={true}
        tier={null}
        show={rewardTarget !== null}
        level={0}
        reward={selectedReward}
        handleSave={handleSaveReward}
        handleClose={() => setRewardTarget(null)}
        disableCurrencySpecialEvent
        disableMonthlyBuff
        disableSubscriptionDay
        enableItemLevelEdit
        liveOpsAvailabilities={[ItemAvailability.EARN]}
      />
      <Card.Header className="text-center">
        <Card.Title>Milestone Templates</Card.Title>
        <Card.Text className="text-muted">
          Reusable milestone presets. Load one into a specific group from that group&apos;s card above.
        </Card.Text>
        <Row>
          <Button className="mr-2" variant="success" onClick={handleAddMilestone}>
            <i className="fas fa-plus" /> Add milestone
          </Button>
          <Button className="mr-2" onClick={handleCreate} disabled={templateName.length === 0}>
            <i className="fas fa-plus" /> Create Template
          </Button>
          <Button className="mr-2" onClick={handleUpdate} disabled={editTemplateId === NONE}>
            <i className="fas fa-edit" /> Update Template
          </Button>
          <Button className="mr-2" variant="danger" onClick={handleDelete} disabled={editTemplateId === NONE}>
            <i className="fas fa-trash" /> Delete Template
          </Button>
          <Button className="mr-2" variant="danger" onClick={() => setMilestones([])}>
            <i className="fas fa-trash" /> Clear Milestones
          </Button>
        </Row>
        <Row>
          <Col className="d-flex flex-column align-items-center">
            <Form.Label className="w-50">Name</Form.Label>
            <Form.Control
              className="w-50"
              type="text"
              value={templateName}
              onChange={(e) => {
                setTemplateName(e.target.value);
              }}
            />
          </Col>
          <Col className="d-flex flex-column align-items-center">
            <Form.Label>Edit Template</Form.Label>
            <Form.Control
              as="select"
              value={editTemplateId}
              className="mb-4 w-50"
              onChange={(e) => handleSelectTemplate(e.target.value)}
            >
              <option key={NONE} value={NONE}>
                {NONE}
              </option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Form.Control>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        {loading ? (
          <LoadingLogo show />
        ) : (
          <MarathonMilestonesTable
            milestones={milestones}
            rowErrors={rowErrors}
            onChangeMilestone={handleChangeMilestone}
            onEditReward={handleEditReward}
            onRemoveRow={handleRemoveMilestone}
          />
        )}
      </Card.Body>
    </Card>
  );
}
